import React from "react";
import type { StatusComponent } from "../../types/status";

type CheckStatus = "pass" | "warn" | "fail";

const checkStatusClass: Record<CheckStatus, string> = {
  pass: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  fail: "bg-red-50 text-red-800 border-red-200",
};

interface HealthCheck {
  status: CheckStatus;
  message: string;
  details?: Record<string, unknown>;
}

interface SslHost {
  host: string;
  status: CheckStatus;
  validTo?: string;
  daysUntilExpiry?: number;
  error?: string;
}

export const StatusDetailsPanel: React.FC<{
  component: StatusComponent;
}> = ({ component }) => {
  const { details, id } = component;
  if (!details) return null;

  if (id === "backend") {
    return <BackendDetails details={details} />;
  }
  if (id === "ssl") {
    return <SslDetails details={details} />;
  }
  if (id === "gps-data") {
    return <GpsDataDetails details={details} />;
  }
  if ("url" in details || "httpStatus" in details) {
    return <HttpServiceDetails details={details} />;
  }
  if ("error" in details && Object.keys(details).length === 1) {
    return <p className="text-sm text-red-700">{String(details.error)}</p>;
  }

  return <JsonFallback details={details} />;
};

const BackendDetails: React.FC<{ details: Record<string, unknown> }> = ({
  details,
}) => {
  const uptime = typeof details.uptime === "number" ? details.uptime : null;
  const checks = details.checks as Record<string, HealthCheck> | undefined;

  return (
    <div className="space-y-3">
      {uptime !== null && (
        <p className="text-sm text-slate-700">
          <span className="font-medium">Uptime:</span> {formatUptime(uptime)}
        </p>
      )}
      {checks && (
        <ul className="space-y-3">
          {Object.entries(checks).map(([name, check]) => (
            <li key={name} className="space-y-1">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 text-sm">
                <span className="font-mono text-xs text-slate-500 w-20 shrink-0">
                  {name}
                </span>
                <CheckBadge status={check.status} />
                <span className="text-slate-600">{check.message}</span>
              </div>
              {check.details && (
                <CheckDetailLines checkName={name} details={check.details} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CheckDetailLines: React.FC<{
  checkName: string;
  details: Record<string, unknown>;
}> = ({ checkName, details }) => {
  if (checkName === "memory") {
    const usageLine = formatDetailParts(details, [
      "total",
      "used",
      "free",
      "percentage",
    ]);
    const processLine = formatDetailParts(details, [
      "processRss",
      "processHeapUsed",
    ]);

    return (
      <div className="pl-24 space-y-0.5 text-xs text-slate-500">
        {usageLine && <p>{usageLine}</p>}
        {processLine && <p>process {processLine}</p>}
      </div>
    );
  }

  if (checkName === "disk") {
    const usageLine = formatDetailParts(details, [
      "total",
      "used",
      "free",
      "percentage",
    ]);

    return usageLine ? (
      <p className="pl-24 text-xs text-slate-500">{usageLine}</p>
    ) : null;
  }

  if (typeof details.error === "string") {
    return <p className="pl-24 text-xs text-red-600">{details.error}</p>;
  }

  return (
    <pre className="ml-24 overflow-x-auto rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
      {JSON.stringify(details, null, 2)}
    </pre>
  );
};

function formatDetailParts(
  details: Record<string, unknown>,
  keys: string[],
): string | null {
  const parts = keys
    .filter((key) => details[key] !== undefined && details[key] !== null)
    .map((key) => `${formatDetailLabel(key)}: ${String(details[key])}`);

  return parts.length > 0 ? parts.join(" | ") : null;
}

function formatDetailLabel(key: string): string {
  switch (key) {
    case "processRss":
      return "RSS";
    case "processHeapUsed":
      return "heap used";
    default:
      return key;
  }
}

const SslDetails: React.FC<{ details: Record<string, unknown> }> = ({
  details,
}) => {
  const hosts = details.hosts as SslHost[] | undefined;
  if (!hosts?.length) return <JsonFallback details={details} />;

  return (
    <ul className="space-y-2">
      {hosts.map((host) => (
        <li
          key={host.host}
          className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 text-sm border-b border-slate-100 pb-2 last:border-0 last:pb-0"
        >
          <span className="font-medium text-slate-900 w-48 shrink-0 truncate">
            {host.host}
          </span>
          <CheckBadge status={host.status} />
          {host.validTo && (
            <span className="text-slate-600">
              Expires {formatDateTime(host.validTo)}
              {host.daysUntilExpiry !== undefined &&
                ` (${host.daysUntilExpiry}d)`}
            </span>
          )}
          {host.error && <span className="text-red-600">{host.error}</span>}
        </li>
      ))}
    </ul>
  );
};

const GpsDataDetails: React.FC<{ details: Record<string, unknown> }> = ({
  details,
}) => {
  const rows: { label: string; value: string }[] = [];

  if (typeof details.lastReceivedAt === "string") {
    rows.push({
      label: "Last received",
      value: formatDateTime(details.lastReceivedAt),
    });
  }
  if (typeof details.ageMinutes === "number") {
    rows.push({
      label: "Age",
      value: `${details.ageMinutes} minutes`,
    });
  }
  if (typeof details.thresholdMinutes === "number") {
    rows.push({
      label: "Threshold",
      value: `${details.thresholdMinutes} minutes`,
    });
  }

  if (rows.length === 0) return <JsonFallback details={details} />;

  return (
    <dl className="grid gap-2 sm:grid-cols-2 text-sm">
      {rows.map(({ label, value }) => (
        <div key={label}>
          <dt className="text-slate-500">{label}</dt>
          <dd className="font-medium text-slate-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

const HttpServiceDetails: React.FC<{ details: Record<string, unknown> }> = ({
  details,
}) => {
  const rows: { label: string; value: string }[] = [];

  if (typeof details.url === "string") {
    rows.push({ label: "URL", value: details.url });
  }
  if (typeof details.httpStatus === "number") {
    rows.push({ label: "HTTP status", value: String(details.httpStatus) });
  }
  if (typeof details.error === "string") {
    rows.push({ label: "Error", value: details.error });
  }

  if (rows.length === 0) return <JsonFallback details={details} />;

  return (
    <p className="text-sm text-slate-700 break-all">
      {rows.map(({ label, value }, index) => (
        <span key={label}>
          {index > 0 && (
            <span className="mx-2 text-slate-300" aria-hidden="true">
              |
            </span>
          )}
          <span className="text-slate-500">{label}:</span>{" "}
          <span className="font-medium text-slate-900">{value}</span>
        </span>
      ))}
    </p>
  );
};

const JsonFallback: React.FC<{ details: Record<string, unknown> }> = ({
  details,
}) => (
  <pre className="overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
    {JSON.stringify(details, null, 2)}
  </pre>
);

const CheckBadge: React.FC<{ status: CheckStatus }> = ({ status }) => (
  <span
    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold shrink-0 ${checkStatusClass[status]}`}
  >
    {status}
  </span>
);

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(" ");
}
